import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { CheckCircle, Phone, Brain, TrendingUp, AlertTriangle, Activity, Heart, Shield, Users, Smartphone } from 'lucide-react';

const RemotePatientMonitoring = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);

  // How It Works journey steps
  const rpmJourney = [
    {
      icon: Phone,
      title: "Automated Check-Ins",
      description: "Scheduled or on-demand voice calls prompt patients to report symptoms and vitals.",
      number: "01"
    },
    {
      icon: Brain,
      title: "AI Analysis",
      description: "Natural language processing extracts health cues—pain levels, medication adherence, breathing difficulty, mood.",
      number: "02"
    },
    {
      icon: TrendingUp,
      title: "Trend Detection",
      description: "Cloud-based analytics flag deviations from baseline, triggering alerts for care teams.",
      number: "03"
    },
    {
      icon: AlertTriangle,
      title: "Seamless Escalation",
      description: "Critical insights are routed via SMS, EMR tasks, or live clinician notifications.",
      number: "04"
    },
    {
      icon: Activity,
      title: "Continuous Learning",
      description: "AI adapts to patient patterns, improving accuracy and personalization over time.",
      number: "05"
    }
  ];

  const keyFeatures = [
    "Voice-First Engagement: No apps—patients simply speak to our AI Nurse for status updates.",
    "Symptom Tracking: Customizable question sets for chronic conditions (e.g., COPD, heart failure, diabetes).",
    "Medication Adherence: Automated reminders and reporting to ensure compliance.",
    "Alert Workflows: Rule-based triggers for incremental or critical thresholds.",
    "Trend Reports: Daily and weekly dashboards for clinicians, integrated into existing EHR."
  ];

  const clinicalBenefits = [
    "Reduce Readmissions: Early intervention lowers 30-day readmission rates.",
    "Improve Outcomes: Continuous oversight fosters better disease management.",
    "Enhance Engagement: Patients feel supported with regular, human-like interactions.",
    "Optimize Resources: Care teams focus on high-risk cases, not routine check-ins."
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < rpmJourney.length - 1) {
      setTimeout(() => {
        setTypingCardIndex(index + 1);
      }, 300);
    }
  };

  React.useEffect(() => {
    // Start the journey when the section comes into view
    const timer = setTimeout(() => {
      setTypingCardIndex(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Remote Patient Monitoring - Proactive Health Insights | Scienza Health</title>
        <meta 
          name="description" 
          content="Voice AI-powered remote patient monitoring that listens, analyzes, and alerts 24/7—no wearables required. Reduce readmissions and improve outcomes." 
        />
      </Helmet>

      <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-depth)' }}>
        {/* Neural Background */}
        <NeuralBackground />
        
        {/* Hero Section */}
        <section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full mb-8">
                <Heart className="w-12 h-12 text-primary" />
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                Proactive Health Insights
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                  Anywhere, Anytime
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-10">
                Voice AI–powered monitoring that listens, analyzes, and alerts 24/7—no wearables required.
              </p>

              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart className="w-5 h-5 mr-3" />
                Learn About RPM
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center">
                  <SynapticText 
                    text="Voice-Powered Remote Monitoring" 
                    delay={300}
                  />
                </h2>
                <SynapticText 
                  text="Scienza Health's Remote Patient Monitoring leverages our voice AI and cloud platform to capture patient status, symptom updates, and wellness signals through simple check-in calls or device interactions. Our AI Nurse and agents gather vital information, detect trends, and escalate concerns before they become crises."
                  delay={1200}
                  className="text-lg text-white leading-relaxed"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="How RPM Works" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Follow the patient monitoring journey from check-in to clinical action"
                delay={1200}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {rpmJourney.map((step, index) => (
                <JourneyCard
                  key={step.title}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  number={step.number}
                  index={index}
                  shouldStartTyping={index === typingCardIndex}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
                <SynapticText 
                  text="Key Features" 
                  delay={200}
                />
              </h2>
              
              <div className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
                
                <div className="relative z-10 space-y-8">
                  {keyFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: 0.3 + index * 0.2,
                        ease: "easeOut"
                      }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-lg sm:text-xl text-white leading-relaxed">
                        <SynapticText 
                          text={feature}
                          delay={600 + index * 400}
                          className="text-lg sm:text-xl text-white leading-relaxed"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
                <SynapticText 
                  text="Clinical Benefits" 
                  delay={200}
                />
              </h2>
              
              <div className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
                
                <div className="relative z-10 space-y-8">
                  {clinicalBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: 0.3 + index * 0.2,
                        ease: "easeOut"
                      }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-lg sm:text-xl text-white leading-relaxed">
                        <SynapticText 
                          text={benefit}
                          delay={600 + index * 400}
                          className="text-lg sm:text-xl text-white leading-relaxed"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Ready for Proactive Patient Care?
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto">
                Transform your remote monitoring capabilities with voice-powered AI that never sleeps, 
                continuously learns, and keeps patients connected to care.
              </p>
              
              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart className="w-5 h-5 mr-3" />
                Schedule an RPM Demo
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RemotePatientMonitoring;